import { Fragment, type RefObject, useEffect, useMemo, useRef, useState } from 'react';
import {
  type ColumnDef,
  type ColumnFiltersState,
  type ColumnOrderState,
  type ColumnSizingState,
  type PaginationState,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
  type Table as TanstackTable,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  EyeOff,
  GripVertical,
  ListFilter,
  RotateCcw,
  SlidersHorizontal,
  WrapText,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Checkbox } from '@/components/ui/Checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import {
  MobileListCard,
  MobileListCardStack,
  type MobileListCardMetaItem,
} from '@/components/ui/MobileListCard';
import {
  MobileTableShell,
  type MobileFilterGroup,
  type MobileSortOption,
} from '@/components/ui/MobileTableShell';
import { useIsMobile } from '@/lib/useMediaQuery';
import { type TableLayout, useSyncedTableLayout } from '@/lib/useSyncedTableLayout';
import { cn } from '@/lib/utils';

export interface FacetedFilter<T = unknown> {
  /** Must match a column id on the table. */
  columnId: string;
  title: string;
  /** Explicit option list; if omitted, options are derived from the facet's
   *  unique values via `formatOption`. */
  options?: Array<{ value: string; label: string; icon?: React.ReactNode }>;
  /** Optional formatter for auto-derived options. */
  formatOption?: (value: T) => { label: string; icon?: React.ReactNode };
}

/**
 * Structured config that drives the mobile-card rendering of each
 * row. Pass this to `DataTable.mobileCard` and the mobile branch
 * (rendered on viewports < md) will lay out a consistent
 * `<MobileListCard>` per row without forcing the caller to fork
 * their cell renderers.
 *
 * Every field is optional. The mobile branch falls back to
 * sensible defaults when something is omitted:
 *   * `title` — first visible non-system text column.
 *   * `subtitle` — second visible non-system column (when present).
 *   * `leading` / `trailing` — derived from `__thumbnail` /
 *     `__open` / `__row_number` system columns when they exist.
 *
 * For more nuanced layouts (financial rows that need a tone-coloured
 * amount, list rows that want a status chip + due date), pass the
 * extractors explicitly — they receive the typed row data and may
 * return any React node.
 */
export interface MobileCardConfig<TData> {
  /** Primary identifier line. Falls back to the first text column. */
  title?: (row: TData) => React.ReactNode;
  /** Optional secondary line (SKU, posting date, customer email). */
  subtitle?: (row: TData) => React.ReactNode;
  /** Chip row (status, kind, priority). Return an array of badge
   *  nodes; the card auto-wraps them. */
  badges?: (row: TData) => Array<React.ReactNode>;
  /** Two-column key-value strip for numeric / dense fields. */
  meta?: (row: TData) => MobileListCardMetaItem[];
  /** Right-aligned bold amount (financial pages). */
  amount?: (row: TData) => {
    value: React.ReactNode;
    tone?: 'positive' | 'negative' | 'neutral';
    caption?: React.ReactNode;
  } | null;
  /** Leading slot — thumbnail, icon, avatar. */
  leading?: (row: TData) => React.ReactNode;
  /** Trailing slot — action button, custom chevron. Pass
   *  `() => null` to suppress the auto-chevron entirely. */
  trailing?: (row: TData) => React.ReactNode;
  /** Per-row dim flag (e.g. de-emphasise posted / archived rows). */
  dimmed?: (row: TData) => boolean;
}

export interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  isLoading?: boolean;
  /** Global filter placeholder; pass null to hide the search box. */
  searchPlaceholder?: string | null;
  /** Faceted multi-select filters rendered next to the search box. */
  facetedFilters?: FacetedFilter[];
  /** Enables a leading checkbox column + bulk-action toolbar slot. */
  enableRowSelection?: boolean;
  /** Page size options for the pagination footer. */
  pageSizes?: number[];
  initialPageSize?: number;
  /** Slot rendered between the toolbar inputs and the right-side controls. */
  toolbarSlot?: React.ReactNode;
  /** Slot rendered to the right of the toolbar (filters/actions). */
  rightSlot?: React.ReactNode;
  /** Slot rendered when `enableRowSelection` and >= 1 row is selected. */
  bulkActions?: (table: TanstackTable<TData>) => React.ReactNode;
  /** Click handler for a non-interactive row tap (e.g., navigate to detail). */
  onRowClick?: (row: TData) => void;
  /** Stable id getter; required for `enableRowSelection` to track across pages. */
  getRowId?: (row: TData, index: number) => string;
  /** Optional per-row className. Composed *after* the cursor / selection
   *  classes so callers can override e.g. the hover background for rows
   *  that are visually de-emphasised (already-posted, archived, …). */
  rowClassName?: (row: TData) => string | undefined;
  /** Optional empty-state slot. */
  emptyState?: React.ReactNode;
  /**
   * Structured mobile-card configuration. When provided, the
   * `< md` branch renders one `<MobileListCard>` per row using
   * these extractors — title / subtitle / badges / meta / amount
   * / leading / trailing — wrapped in `<MobileTableShell>` for
   * the search toolbar, bottom-sheet filters, and load-more
   * pagination. This is the preferred mobile API.
   */
  mobileCard?: MobileCardConfig<TData>;
  /**
   * Optional render function for a fully custom mobile card. Takes
   * precedence over `mobileCard` when both are provided. Use this
   * only when the structured slots don't fit — most pages should
   * use `mobileCard` so the mobile chrome (selection state,
   * dimming, chevron behavior) stays consistent.
   */
  mobileRenderer?: (row: TData) => React.ReactNode;
  /**
   * Initial number of rows to render on mobile before showing a
   * "Load more" button. Defaults to `initialPageSize`. The
   * pagination footer is replaced on mobile with this load-more
   * pattern because the desktop's chevron-button pager has
   * sub-44 px tap targets.
   */
  mobileInitialCount?: number;
  /**
   * How many additional rows each "Load more" tap appends. Defaults
   * to `mobileInitialCount`.
   */
  mobileLoadMoreStep?: number;
  /** Lets users drag column headers to reorder them (desktop only). On by
   *  default. System columns whose id begins with `__` (e.g. selection
   *  checkbox, row-action chevron) stay pinned and are not draggable. */
  enableColumnReordering?: boolean;
  /** Lets users drag the right edge of a column header to resize it
   *  (double-click the edge to restore the default width). On by default.
   *  System columns (id begins with `__`) are not resizable. Individual
   *  columns can opt out via `enableResizing: false` on the column def. */
  enableColumnResizing?: boolean;
  /** Stable identifier used to persist user-chosen column order, widths,
   *  visibility, and view state. Layout is saved to `localStorage` and,
   *  when signed in, to the user's `user_preferences` so it follows them
   *  across browsers and devices. Required for any of it to survive a
   *  page reload. */
  tableId?: string;
  /** Map of column id → initial visibility. Used as the default when
   *  there is nothing stored in `localStorage` for this `tableId` (or
   *  when `tableId` is omitted). Columns the user later toggles via the
   *  "View" menu override these defaults and persist. */
  initialColumnVisibility?: VisibilityState;
  /** Show a toolbar toggle that lets the user flip cell text wrapping
   *  on/off. With wrap on (default) long values flow to additional
   *  lines inside their cell; with wrap off long values are truncated
   *  to a single line with an ellipsis. Persists per-`tableId` to
   *  `localStorage` when one is provided. */
  enableTextWrapToggle?: boolean;
  /** Initial value of the text-wrap toggle when there is nothing stored
   *  in `localStorage` for this `tableId`. Defaults to `true` (wrap on). */
  initialTextWrap?: boolean;
}

/** localStorage key prefix for persisted column order. Bumping the version
 *  segment will invalidate stored orders if we ever change the schema. */
const COLUMN_ORDER_STORAGE_PREFIX = 'atlas:dt:colOrder:v1:';

/** localStorage key prefix for persisted column widths. Same versioning
 *  story as the column-order key — bump the segment to invalidate. */
const COLUMN_SIZING_STORAGE_PREFIX = 'atlas:dt:colSize:v1:';

/** localStorage key prefix for persisted column visibility (the "View"
 *  dropdown toggles). Same versioning story as the column-order key. */
const COLUMN_VISIBILITY_STORAGE_PREFIX = 'atlas:dt:colVis:v1:';

/** localStorage key prefix for the per-table "wrap text" toggle. Same
 *  versioning story as the column-order key. */
const TEXT_WRAP_STORAGE_PREFIX = 'atlas:dt:wrap:v1:';

/** localStorage key prefix for the persisted sort state (which column,
 *  ascending or descending). Same versioning story as the column-order
 *  key — bump the segment to invalidate stored entries. */
const SORTING_STORAGE_PREFIX = 'atlas:dt:sort:v1:';

/** localStorage key prefix for the persisted faceted column filters
 *  (the multi-select dropdowns to the right of the search box). */
const COLUMN_FILTERS_STORAGE_PREFIX = 'atlas:dt:colFilters:v1:';

/** localStorage key prefix for the persisted global search box value. */
const GLOBAL_FILTER_STORAGE_PREFIX = 'atlas:dt:globalFilter:v1:';

/** localStorage key prefix for the persisted pagination state (page
 *  index + page size). Stored together because they're a single
 *  pair in TanStack's state and the rows-per-page picker writes to
 *  both at once. */
const PAGINATION_STORAGE_PREFIX = 'atlas:dt:pagination:v1:';

/** Columns whose id starts with this prefix are treated as "system" columns
 *  (selection checkbox, row-action chevrons, etc.) — they keep their
 *  natural position in the header row and are not user-reorderable. */
const SYSTEM_COLUMN_PREFIX = '__';

/**
 * Reconcile a persisted column order against the currently declared column
 * set. System columns (`__`-prefixed) always sit at the position the caller
 * declared them in — even if a stale localStorage payload omits them or
 * places them elsewhere — because they're never user-reorderable and adding
 * a new one (or moving one in code) needs to "just work" for existing users.
 * User-orderable columns keep their saved relative positions; any user
 * column introduced in a later release is appended at the end of the
 * user-orderable section.
 */
function reconcileColumnOrder(saved: string[], declared: string[]): string[] {
  const isSystem = (id: string) => id.startsWith(SYSTEM_COLUMN_PREFIX);
  const declaredSet = new Set(declared);
  const declaredUser = declared.filter((id) => !isSystem(id));
  const savedUser = saved.filter(
    (id) => declaredSet.has(id) && !isSystem(id),
  );
  const missingUser = declaredUser.filter((id) => !savedUser.includes(id));
  const userOrder = [...savedUser, ...missingUser];

  const result: string[] = [];
  let userIdx = 0;
  for (const id of declared) {
    if (isSystem(id)) {
      result.push(id);
    } else {
      result.push(userOrder[userIdx++] ?? id);
    }
  }
  return result;
}

/**
 * Drop persisted visibility entries for columns that no longer exist (so a
 * stale toggle for a removed column can't shadow a freshly-added one with
 * the same id), then merge caller defaults UNDER what's left so a new
 * column declared with an `initialColumnVisibility` entry shows up
 * correctly on first encounter while existing toggles win.
 */
function reconcileColumnVisibility(
  saved: VisibilityState,
  declared: string[],
  initial: VisibilityState | undefined,
): VisibilityState {
  const known = new Set(declared);
  const result: VisibilityState = {};
  for (const [id, visible] of Object.entries(saved)) {
    if (known.has(id)) result[id] = visible;
  }
  if (initial) {
    for (const [id, visible] of Object.entries(initial)) {
      if (!(id in result)) result[id] = visible;
    }
  }
  return result;
}

const MIN_COLUMN_WIDTH = 48;
const MAX_COLUMN_WIDTH = 1200;

/**
 * Sort/filter/paginate/select DataTable powered by TanStack Table v8 and
 * rendered through the shadcn `Table` primitives. Stays headless: callers
 * declare columns + meta and this wrapper drives all the state.
 */
export function DataTable<TData, TValue>(props: DataTableProps<TData, TValue>) {
  // Persisted state is read once on mount, so a page that stays mounted
  // while its `tableId` changes (e.g. switching item kinds in the sidebar)
  // must remount; otherwise one table's layout is saved under the other's id.
  return <DataTableInner<TData, TValue> key={props.tableId ?? ''} {...props} />;
}

function DataTableInner<TData, TValue>({
  columns: userColumns,
  data,
  isLoading,
  searchPlaceholder = 'Search…',
  facetedFilters = [],
  enableRowSelection = false,
  pageSizes = [10, 25, 50, 100],
  initialPageSize = 25,
  toolbarSlot,
  rightSlot,
  bulkActions,
  onRowClick,
  getRowId,
  rowClassName,
  emptyState,
  mobileCard,
  mobileRenderer,
  mobileInitialCount,
  mobileLoadMoreStep,
  enableColumnReordering = true,
  enableColumnResizing = true,
  tableId,
  initialColumnVisibility,
  enableTextWrapToggle = false,
  initialTextWrap = true,
}: DataTableProps<TData, TValue>) {
  const isMobile = useIsMobile();

  // Persisted view state — sorting, column filters, the global search
  // box, and pagination — all keyed off `tableId` so each table gets
  // its own bucket and a click-into-detail-then-back trip preserves
  // exactly what the operator was looking at. Without this the
  // component would unmount on navigation and reset to defaults.
  const sortingStorageKey = tableId
    ? `${SORTING_STORAGE_PREFIX}${tableId}`
    : null;
  const columnFiltersStorageKey = tableId
    ? `${COLUMN_FILTERS_STORAGE_PREFIX}${tableId}`
    : null;
  const globalFilterStorageKey = tableId
    ? `${GLOBAL_FILTER_STORAGE_PREFIX}${tableId}`
    : null;
  const paginationStorageKey = tableId
    ? `${PAGINATION_STORAGE_PREFIX}${tableId}`
    : null;

  const [sorting, setSorting] = useState<SortingState>(() => {
    if (!sortingStorageKey) return [];
    try {
      const raw = window.localStorage.getItem(sortingStorageKey);
      if (raw) {
        const parsed = JSON.parse(raw) as unknown;
        if (
          Array.isArray(parsed) &&
          parsed.every(
            (s): s is { id: string; desc: boolean } =>
              !!s &&
              typeof s === 'object' &&
              typeof (s as { id?: unknown }).id === 'string' &&
              typeof (s as { desc?: unknown }).desc === 'boolean',
          )
        ) {
          return parsed;
        }
      }
    } catch {
      // localStorage may be unavailable — fall through to no sort.
    }
    return [];
  });

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(() => {
    if (!columnFiltersStorageKey) return [];
    try {
      const raw = window.localStorage.getItem(columnFiltersStorageKey);
      if (raw) {
        const parsed = JSON.parse(raw) as unknown;
        if (
          Array.isArray(parsed) &&
          parsed.every(
            (f): f is { id: string; value: unknown } =>
              !!f &&
              typeof f === 'object' &&
              typeof (f as { id?: unknown }).id === 'string' &&
              'value' in (f as object),
          )
        ) {
          return parsed;
        }
      }
    } catch {
      // localStorage may be unavailable — fall through to no filters.
    }
    return [];
  });

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const [globalFilter, setGlobalFilter] = useState<string>(() => {
    if (!globalFilterStorageKey) return '';
    try {
      const raw = window.localStorage.getItem(globalFilterStorageKey);
      // Stored as a JSON string so empty / non-string values survive
      // the round-trip cleanly.
      if (raw !== null) {
        const parsed = JSON.parse(raw) as unknown;
        if (typeof parsed === 'string') return parsed;
      }
    } catch {
      // localStorage may be unavailable — fall through to empty.
    }
    return '';
  });

  // Pagination is controlled here (rather than via TanStack's
  // `initialState`) so we can persist `pageIndex` + `pageSize` as a
  // pair. Coming back from a detail page on row 3 of page 5 should
  // land you back on page 5.
  const [pagination, setPagination] = useState<PaginationState>(() => {
    const fallback: PaginationState = { pageIndex: 0, pageSize: initialPageSize };
    if (!paginationStorageKey) return fallback;
    try {
      const raw = window.localStorage.getItem(paginationStorageKey);
      if (raw) {
        const parsed = JSON.parse(raw) as unknown;
        if (
          parsed &&
          typeof parsed === 'object' &&
          !Array.isArray(parsed) &&
          typeof (parsed as { pageIndex?: unknown }).pageIndex === 'number' &&
          Number.isFinite((parsed as { pageIndex: number }).pageIndex) &&
          typeof (parsed as { pageSize?: unknown }).pageSize === 'number' &&
          Number.isFinite((parsed as { pageSize: number }).pageSize)
        ) {
          return parsed as PaginationState;
        }
      }
    } catch {
      // localStorage may be unavailable — fall through to defaults.
    }
    return fallback;
  });

  useEffect(() => {
    if (!sortingStorageKey) return;
    try {
      window.localStorage.setItem(sortingStorageKey, JSON.stringify(sorting));
    } catch {
      // Quota exceeded / disabled — best-effort persistence only.
    }
  }, [sortingStorageKey, sorting]);

  useEffect(() => {
    if (!columnFiltersStorageKey) return;
    try {
      window.localStorage.setItem(
        columnFiltersStorageKey,
        JSON.stringify(columnFilters),
      );
    } catch {
      // Quota exceeded / disabled — best-effort persistence only.
    }
  }, [columnFiltersStorageKey, columnFilters]);

  useEffect(() => {
    if (!globalFilterStorageKey) return;
    try {
      window.localStorage.setItem(
        globalFilterStorageKey,
        JSON.stringify(globalFilter),
      );
    } catch {
      // Quota exceeded / disabled — best-effort persistence only.
    }
  }, [globalFilterStorageKey, globalFilter]);

  useEffect(() => {
    if (!paginationStorageKey) return;
    try {
      window.localStorage.setItem(
        paginationStorageKey,
        JSON.stringify(pagination),
      );
    } catch {
      // Quota exceeded / disabled — best-effort persistence only.
    }
  }, [paginationStorageKey, pagination]);

  // Prepend a select column when row selection is on, so callers don't
  // have to copy/paste the checkbox boilerplate per page.
  const columns = useMemo<ColumnDef<TData, TValue>[]>(() => {
    if (!enableRowSelection) return userColumns;
    const selectColumn: ColumnDef<TData, TValue> = {
      id: '__select',
      enableSorting: false,
      enableHiding: false,
      enableResizing: false,
      size: 36,
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected()
              ? true
              : table.getIsSomePageRowsSelected()
                ? 'indeterminate'
                : false
          }
          onCheckedChange={(v) => table.toggleAllPageRowsSelected(!!v)}
          ariaLabel="Select all rows on this page"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(v) => row.toggleSelected(!!v)}
          ariaLabel="Select row"
        />
      ),
    };
    return [selectColumn, ...userColumns];
  }, [userColumns, enableRowSelection]);

  // The "default" column order is whatever the caller declared. We need it
  // (a) to seed the controlled `columnOrder` state when reordering is on,
  // and (b) to reconcile a persisted order against the current column set.
  // Falls back from `id` → `accessorKey` so callers who omit explicit ids
  // still produce a usable order array.
  const defaultColumnOrder = useMemo<string[]>(
    () =>
      columns
        .map((c) => {
          if (c.id) return c.id;
          const ak = (c as { accessorKey?: unknown }).accessorKey;
          return typeof ak === 'string' ? ak : '';
        })
        .filter(Boolean),
    [columns],
  );
  const storageKey =
    enableColumnReordering && tableId ? `${COLUMN_ORDER_STORAGE_PREFIX}${tableId}` : null;

  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(() => {
    if (!enableColumnReordering) return [];
    if (!storageKey) return defaultColumnOrder;
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw) as unknown;
        if (
          Array.isArray(parsed) &&
          parsed.every((v): v is string => typeof v === 'string')
        ) {
          return reconcileColumnOrder(parsed, defaultColumnOrder);
        }
      }
    } catch {
      // localStorage may be unavailable (private mode, SSR snapshot, etc.) —
      // fall through to the default column order.
    }
    return defaultColumnOrder;
  });

  // If the underlying column set changes at runtime (rare, but happens when a
  // page conditionally adds a column), keep our order in sync without losing
  // the user's relative positioning of the columns we still recognise.
  useEffect(() => {
    if (!enableColumnReordering) return;
    setColumnOrder((prev) => {
      const next = reconcileColumnOrder(prev, defaultColumnOrder);
      // Avoid a no-op state update that would just churn renders.
      const same =
        next.length === prev.length && next.every((id, i) => id === prev[i]);
      return same ? prev : next;
    });
  }, [defaultColumnOrder, enableColumnReordering]);

  // Persist user-driven order changes. The hydration path above does the
  // initial read, so this only writes when the order actually mutates.
  useEffect(() => {
    if (!storageKey) return;
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(columnOrder));
    } catch {
      // Quota exceeded / disabled — best-effort persistence only.
    }
  }, [storageKey, columnOrder]);

  // ───────────────────────── Column sizing ─────────────────────────
  // Mirror of the column-order persistence pattern: hydrate from
  // localStorage on first render, write back on every change. Stored
  // value is a `{ [columnId]: width }` map (TanStack's native shape).
  const sizingStorageKey =
    enableColumnResizing && tableId ? `${COLUMN_SIZING_STORAGE_PREFIX}${tableId}` : null;

  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(() => {
    if (!enableColumnResizing || !sizingStorageKey) return {};
    try {
      const raw = window.localStorage.getItem(sizingStorageKey);
      if (raw) {
        const parsed = JSON.parse(raw) as unknown;
        if (
          parsed &&
          typeof parsed === 'object' &&
          !Array.isArray(parsed) &&
          Object.values(parsed as Record<string, unknown>).every(
            (v) => typeof v === 'number' && Number.isFinite(v),
          )
        ) {
          return parsed as ColumnSizingState;
        }
      }
    } catch {
      // localStorage may be unavailable — fall through to defaults.
    }
    return {};
  });

  useEffect(() => {
    if (!sizingStorageKey) return;
    try {
      window.localStorage.setItem(sizingStorageKey, JSON.stringify(columnSizing));
    } catch {
      // Quota exceeded / disabled — best-effort persistence only.
    }
  }, [sizingStorageKey, columnSizing]);

  // ─────────────────────── Column visibility ───────────────────────
  // Same hydrate-on-mount / write-on-change pattern as column order
  // and sizing above. Keys off `tableId` so each table gets its own
  // bucket.
  const visibilityStorageKey = tableId
    ? `${COLUMN_VISIBILITY_STORAGE_PREFIX}${tableId}`
    : null;

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() => {
    // 1. Try localStorage first — preserves the user's view between
    //    sessions for tables with a stable `tableId`.
    if (visibilityStorageKey) {
      try {
        const raw = window.localStorage.getItem(visibilityStorageKey);
        if (raw) {
          const parsed = JSON.parse(raw) as unknown;
          if (
            parsed &&
            typeof parsed === 'object' &&
            !Array.isArray(parsed) &&
            Object.values(parsed as Record<string, unknown>).every(
              (v) => typeof v === 'boolean',
            )
          ) {
            return reconcileColumnVisibility(
              parsed as VisibilityState,
              defaultColumnOrder,
              initialColumnVisibility,
            );
          }
        }
      } catch {
        // localStorage may be unavailable — fall through to defaults.
      }
    }
    // 2. No persisted state — use the caller's defaults.
    return initialColumnVisibility ?? {};
  });

  useEffect(() => {
    if (!visibilityStorageKey) return;
    try {
      window.localStorage.setItem(
        visibilityStorageKey,
        JSON.stringify(columnVisibility),
      );
    } catch {
      // Quota exceeded / disabled — best-effort persistence only.
    }
  }, [visibilityStorageKey, columnVisibility]);

  // ─────────────────────────── Text wrap ───────────────────────────
  // Mirrors the other per-table preferences: hydrate from localStorage
  // on first render, write back on every change. Only active when the
  // caller opts in via `enableTextWrapToggle` so existing tables see
  // no behavioural change.
  const textWrapStorageKey =
    enableTextWrapToggle && tableId
      ? `${TEXT_WRAP_STORAGE_PREFIX}${tableId}`
      : null;

  const [textWrap, setTextWrap] = useState<boolean>(() => {
    if (!enableTextWrapToggle) return true;
    if (!textWrapStorageKey) return initialTextWrap;
    try {
      const raw = window.localStorage.getItem(textWrapStorageKey);
      if (raw === 'true') return true;
      if (raw === 'false') return false;
    } catch {
      // localStorage may be unavailable — fall through to the default.
    }
    return initialTextWrap;
  });

  useEffect(() => {
    if (!textWrapStorageKey) return;
    try {
      window.localStorage.setItem(textWrapStorageKey, String(textWrap));
    } catch {
      // Quota exceeded / disabled — best-effort persistence only.
    }
  }, [textWrapStorageKey, textWrap]);

  // ───────────────────── Cross-device layout sync ─────────────────────
  const defaultLayout: TableLayout = {
    order: enableColumnReordering ? defaultColumnOrder : [],
    sizing: {},
    visibility: initialColumnVisibility ?? {},
  };

  const resetColumnLayout = () => {
    if (enableColumnReordering) setColumnOrder(defaultColumnOrder);
    setColumnSizing({});
    setColumnVisibility(initialColumnVisibility ?? {});
  };

  useSyncedTableLayout({
    tableId,
    layout: {
      order: enableColumnReordering ? columnOrder : [],
      sizing: enableColumnResizing ? columnSizing : {},
      visibility: columnVisibility,
    },
    defaults: defaultLayout,
    apply: (remote) => {
      if (enableColumnReordering && remote.order) {
        setColumnOrder(reconcileColumnOrder(remote.order, defaultColumnOrder));
      }
      if (enableColumnResizing && remote.sizing) setColumnSizing(remote.sizing);
      if (remote.visibility) {
        setColumnVisibility(
          reconcileColumnVisibility(
            remote.visibility,
            defaultColumnOrder,
            initialColumnVisibility,
          ),
        );
      }
    },
  });

  const layoutCustomized =
    JSON.stringify(columnSizing) !== '{}' ||
    (enableColumnReordering &&
      JSON.stringify(columnOrder) !== JSON.stringify(defaultColumnOrder)) ||
    JSON.stringify(columnVisibility) !==
      JSON.stringify(initialColumnVisibility ?? {});

  const table = useReactTable({
    data,
    columns,
    // Only feed `columnOrder` to TanStack when reordering is enabled.
    // Otherwise we let TanStack manage order internally so existing tables
    // that don't opt in see no behavioural change.
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      globalFilter,
      pagination,
      ...(enableColumnReordering ? { columnOrder } : {}),
      ...(enableColumnResizing ? { columnSizing } : {}),
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    ...(enableColumnReordering ? { onColumnOrderChange: setColumnOrder } : {}),
    ...(enableColumnResizing
      ? {
          onColumnSizingChange: setColumnSizing,
          enableColumnResizing: true,
        }
      : {}),
    enableRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId,
    globalFilterFn: 'includesString',
    // TanStack's default resets to page 1 whenever `data` changes, which
    // throws away the restored page on every load and refetch. Resetting
    // on filter / sort changes is handled below instead.
    autoResetPageIndex: false,
  });

  const tableScrollRef = useRef<HTMLDivElement>(null);

  const viewFilterKey = JSON.stringify([globalFilter, columnFilters, sorting]);
  const prevViewFilterKeyRef = useRef(viewFilterKey);
  useEffect(() => {
    if (prevViewFilterKeyRef.current === viewFilterKey) return;
    prevViewFilterKeyRef.current = viewFilterKey;
    setPagination((p) => (p.pageIndex === 0 ? p : { ...p, pageIndex: 0 }));
  }, [viewFilterKey]);

  const pageCount = table.getPageCount();
  useEffect(() => {
    if (isLoading || data.length === 0) return;
    const lastPage = Math.max(0, pageCount - 1);
    setPagination((p) => (p.pageIndex > lastPage ? { ...p, pageIndex: lastPage } : p));
  }, [isLoading, data.length, pageCount]);

  // Native HTML5 drag-and-drop powers the column reordering UX. We chose
  // this over a DnD library because (a) the project's existing DnD lib
  // (@hello-pangea/dnd) doesn't play well with horizontal table headers
  // (transformed `<th>` elements break table layout), and (b) native DnD
  // adds zero bundle weight and works perfectly for a desktop-first ERP.
  // It silently no-ops on touch, which is fine — mobile uses `mobileRenderer`.
  const [draggingColId, setDraggingColId] = useState<string | null>(null);
  const [dragOverColId, setDragOverColId] = useState<string | null>(null);

  const isColumnReorderable = (id: string) =>
    enableColumnReordering && !id.startsWith(SYSTEM_COLUMN_PREFIX);

  const isColumnResizable = (id: string) =>
    enableColumnResizing && !id.startsWith(SYSTEM_COLUMN_PREFIX);

  // Resizing is driven by pointer events rather than TanStack's
  // `getResizeHandler`, because TanStack starts the drag from the
  // column's declared size (150 by default) while auto-layout columns
  // usually render wider or narrower than that — the column would jump
  // on mousedown. Measuring the rendered <th> keeps the edge under the
  // cursor.
  const [resizingColId, setResizingColId] = useState<string | null>(null);

  const handleResizePointerDown = (
    e: React.PointerEvent<HTMLDivElement>,
    colId: string,
  ) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const th = e.currentTarget.parentElement;
    if (!th) return;
    const startX = e.clientX;
    const startWidth = th.getBoundingClientRect().width;
    const handle = e.currentTarget;
    handle.setPointerCapture(e.pointerId);
    setResizingColId(colId);

    const onMove = (ev: PointerEvent) => {
      const width = Math.round(
        Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, startWidth + ev.clientX - startX)),
      );
      setColumnSizing((prev) => (prev[colId] === width ? prev : { ...prev, [colId]: width }));
    };
    const onUp = () => {
      handle.removeEventListener('pointermove', onMove);
      handle.removeEventListener('pointerup', onUp);
      handle.removeEventListener('pointercancel', onUp);
      setResizingColId(null);
    };
    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onUp);
    handle.addEventListener('pointercancel', onUp);
  };

  const resetColumnWidth = (colId: string) => {
    setColumnSizing((prev) => {
      if (!(colId in prev)) return prev;
      const next = { ...prev };
      delete next[colId];
      return next;
    });
  };

  // Only pin a width when the caller declared one or the user resized the
  // column; everything else keeps the browser's auto layout so tables
  // look the same until someone actually customises them.
  const columnWidthStyle = (colId: string, size: number) =>
    colId in columnSizing || size !== 150 ? size : undefined;

  const handleColDragStart = (e: React.DragEvent<HTMLTableCellElement>, id: string) => {
    if (!isColumnReorderable(id)) return;
    setDraggingColId(id);
    e.dataTransfer.effectAllowed = 'move';
    // Firefox refuses to start a drag without payload data on the event.
    e.dataTransfer.setData('text/plain', id);
  };

  const handleColDragOver = (e: React.DragEvent<HTMLTableCellElement>, id: string) => {
    if (!draggingColId || !isColumnReorderable(id) || draggingColId === id) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColId(id);
  };

  const handleColDrop = (e: React.DragEvent<HTMLTableCellElement>, targetId: string) => {
    if (!draggingColId || !isColumnReorderable(targetId) || draggingColId === targetId) {
      return;
    }
    e.preventDefault();
    const sourceId = draggingColId;
    setColumnOrder((prev) => {
      const next = prev.filter((id) => id !== sourceId);
      const targetIdx = next.indexOf(targetId);
      // If the target id is unexpectedly absent (defensive), just no-op.
      if (targetIdx === -1) return prev;
      next.splice(targetIdx, 0, sourceId);
      return next;
    });
    setDraggingColId(null);
    setDragOverColId(null);
  };

  const handleColDragEnd = () => {
    setDraggingColId(null);
    setDragOverColId(null);
  };

  const selectedCount = Object.keys(rowSelection).length;
  const filterActive =
    !!globalFilter || columnFilters.length > 0 || sorting.length > 0;

  // ─────────────────────────────────────────────────────────────────
  // Mobile branch — when `useIsMobile()` flips true we short-circuit
  // out of the desktop tree entirely and render the Asana/Linear
  // style card list inside `MobileTableShell`. Doing this as an
  // early return (instead of CSS-toggled siblings) means the heavy
  // desktop header / column-resize logic doesn't run at all on a
  // phone — so a 1000-row Transactions table never mounts 8000+
  // TableCell DOM nodes only to hide them.
  //
  // We *intentionally* still rely on `useReactTable`'s state for
  // sorting / filtering / selection — the mobile toolbar is just a
  // different surface that pushes the same actions into the same
  // table instance. That keeps the two views perfectly in sync if
  // the user rotates their tablet from portrait to landscape and
  // crosses the 768 px breakpoint mid-session.
  // ─────────────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <MobileDataTable
        table={table}
        isLoading={isLoading}
        searchPlaceholder={searchPlaceholder}
        facetedFilters={facetedFilters}
        enableRowSelection={enableRowSelection}
        enableTextWrapToggle={enableTextWrapToggle}
        textWrap={textWrap}
        setTextWrap={setTextWrap}
        onRowClick={onRowClick}
        rowClassName={rowClassName}
        emptyState={emptyState}
        mobileCard={mobileCard}
        mobileRenderer={mobileRenderer}
        mobileInitialCount={mobileInitialCount ?? initialPageSize}
        mobileLoadMoreStep={mobileLoadMoreStep ?? mobileInitialCount ?? initialPageSize}
        bulkActions={bulkActions}
        pageActions={rightSlot}
        toolbarSlot={toolbarSlot}
        onReset={() => {
          setGlobalFilter('');
          setColumnFilters([]);
          setSorting([]);
        }}
      />
    );
  }

  return (
    <div className="space-y-3">
      {/* ───────────────────────── Toolbar ───────────────────────── */}
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        {searchPlaceholder !== null && (
          <Input
            value={globalFilter}
            placeholder={searchPlaceholder}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className="sm:max-w-xs"
          />
        )}

        {facetedFilters.map((f) => (
          <FacetedFilterDropdown key={f.columnId} table={table} filter={f} />
        ))}

        {toolbarSlot}

        {filterActive && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setGlobalFilter('');
              setColumnFilters([]);
              setSorting([]);
            }}
          >
            Reset
            <X className="h-3.5 w-3.5" />
          </Button>
        )}

        <div className="flex-1" />

        {rightSlot}

        {enableTextWrapToggle && (
          <Button
            variant={textWrap ? 'default' : 'outline'}
            size="sm"
            onClick={() => setTextWrap((v) => !v)}
            aria-pressed={textWrap}
            title={
              textWrap
                ? 'Wrap text is on — click to truncate long values to one line'
                : 'Wrap text is off — click to let long values wrap'
            }
          >
            <WrapText className="h-3.5 w-3.5" />
            {textWrap ? 'Wrap: on' : 'Wrap: off'}
          </Button>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <SlidersHorizontal className="h-3.5 w-3.5" />
              View
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {table
              .getAllLeafColumns()
              .filter((c) => c.getCanHide())
              .map((c) => (
                <DropdownMenuCheckboxItem
                  key={c.id}
                  checked={c.getIsVisible()}
                  onCheckedChange={(v) => c.toggleVisibility(!!v)}
                >
                  {typeof c.columnDef.header === 'string' ? c.columnDef.header : c.id}
                </DropdownMenuCheckboxItem>
              ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem disabled={!layoutCustomized} onSelect={resetColumnLayout}>
              <RotateCcw className="h-3.5 w-3.5" />
              Reset columns
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* ───────────────────── Bulk-action toolbar ──────────────────── */}
      {enableRowSelection && selectedCount > 0 && (
        <div className="flex items-center justify-between rounded-lg border border-border bg-secondary/30 px-3 py-2">
          <div className="flex items-center gap-2 text-sm">
            <Badge variant="default">{selectedCount}</Badge>
            <span className="text-muted-foreground">
              {selectedCount === 1 ? 'row' : 'rows'} selected
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => table.resetRowSelection()}
            >
              Clear
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
          <div className="flex items-center gap-2">{bulkActions?.(table)}</div>
        </div>
      )}

      {/* ───────────────────────── Table ───────────────────────── */}
      {/* No more `hidden md:block` here — the mobile branch is an
          early return above, so on phones this whole subtree is
          never mounted. */}
      <div className="rounded-lg border border-border">
        <TopScrollbar targetRef={tableScrollRef} />
        <Table
          wrapperRef={tableScrollRef}
          className={cn(
            // When wrap is off, force fixed layout so columns honour
            // their declared widths and the truncate rule below has
            // a width to clip against. The `whitespace-nowrap` and
            // `overflow-hidden` on every body cell keep nested
            // content (Selects, badges, multi-element flex cells) on
            // a single visual line and silently clip the overflow.
            !textWrap &&
              'table-fixed [&_tbody_td]:overflow-hidden [&_tbody_td]:whitespace-nowrap [&_tbody_td]:text-ellipsis [&_tbody_td]:align-middle',
          )}
        >
          <TableHeader>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((header) => {
                  const colId = header.column.id;
                  const reorderable = isColumnReorderable(colId);
                  const resizable = isColumnResizable(colId) && header.column.getCanResize();
                  // Visual indicator: when dragging, mark the hovered target
                  // column with a coloured edge on the side the column will
                  // land on (left if moving from right→left, right otherwise).
                  let dropEdge: 'left' | 'right' | null = null;
                  if (
                    reorderable &&
                    draggingColId &&
                    dragOverColId === colId &&
                    draggingColId !== colId
                  ) {
                    const fromIdx = columnOrder.indexOf(draggingColId);
                    const toIdx = columnOrder.indexOf(colId);
                    dropEdge = fromIdx > toIdx ? 'left' : 'right';
                  }
                  const widthStyle = columnWidthStyle(colId, header.getSize());
                  return (
                    <TableHead
                      key={header.id}
                      style={{
                        width: widthStyle,
                        // Needed so the absolutely-positioned resize
                        // handle anchors to the header's right edge.
                        position: resizable ? 'relative' : undefined,
                      }}
                      draggable={reorderable && resizingColId === null}
                      onDragStart={
                        reorderable ? (e) => handleColDragStart(e, colId) : undefined
                      }
                      onDragOver={
                        reorderable ? (e) => handleColDragOver(e, colId) : undefined
                      }
                      onDrop={
                        reorderable ? (e) => handleColDrop(e, colId) : undefined
                      }
                      onDragEnd={reorderable ? handleColDragEnd : undefined}
                      className={cn(
                        reorderable && 'group/col',
                        draggingColId === colId && 'opacity-50',
                        // Inset box-shadow rather than a real border so the
                        // table layout doesn't shift by 2px during a drag.
                        dropEdge === 'left' && 'shadow-[inset_2px_0_0_0_hsl(var(--primary))]',
                        dropEdge === 'right' && 'shadow-[inset_-2px_0_0_0_hsl(var(--primary))]',
                      )}
                    >
                      {header.isPlaceholder ? null : (
                        // `whitespace-nowrap` on the header keeps column
                        // titles tight on a single line — matches QBO's
                        // bank-register layout where headers never wrap
                        // even when a body column does.
                        <span className="inline-flex items-center gap-1 whitespace-nowrap">
                          {reorderable && (
                            <GripVertical
                              aria-hidden="true"
                              className="h-3 w-3 cursor-grab text-muted-foreground/40 opacity-0 transition-opacity group-hover/col:opacity-100 active:cursor-grabbing"
                            />
                          )}
                          {header.column.getCanSort() ? (
                            <button
                              type="button"
                              onClick={header.column.getToggleSortingHandler()}
                              className="-mx-1 inline-flex items-center gap-1 whitespace-nowrap rounded px-1 py-0.5 hover:bg-secondary/60"
                            >
                              {flexRender(header.column.columnDef.header, header.getContext())}
                              <SortIcon dir={header.column.getIsSorted()} />
                            </button>
                          ) : (
                            flexRender(header.column.columnDef.header, header.getContext())
                          )}
                        </span>
                      )}
                      {resizable && (
                        // Thin hit target on the column's right edge.
                        // `preventDefault` on mousedown stops the browser
                        // from starting an HTML5 drag on the (possibly
                        // draggable-for-reorder) parent <th>, and
                        // `stopPropagation` keeps the same mousedown from
                        // bubbling up and triggering reorder state.
                        <div
                          role="separator"
                          aria-orientation="vertical"
                          aria-label={`Resize ${colId} column`}
                          title="Drag to resize · double-click to reset"
                          onPointerDown={(e) => handleResizePointerDown(e, colId)}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onDoubleClick={(e) => {
                            e.stopPropagation();
                            resetColumnWidth(colId);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          onDragStart={(e) => e.preventDefault()}
                          className={cn(
                            'absolute right-0 top-0 z-10 h-full w-1.5 cursor-col-resize select-none touch-none',
                            'bg-transparent transition-colors hover:bg-primary/40',
                            resizingColId === colId && 'bg-primary',
                          )}
                        />
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="py-12 text-center text-muted-foreground">
                  Loading…
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="py-12 text-center">
                  {emptyState ?? (
                    <span className="text-sm text-muted-foreground">No results.</span>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() ? 'selected' : undefined}
                  className={cn(
                    onRowClick && 'cursor-pointer',
                    rowClassName?.(row.original),
                  )}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                >
                  {row.getVisibleCells().map((cell) => {
                    return (
                      <TableCell
                        key={cell.id}
                        style={{
                          width: columnWidthStyle(cell.column.id, cell.column.getSize()),
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* The old `mobileRenderer` "second tree" lived here as a
          CSS-toggled `<div className="md:hidden">` sibling. That
          branch has been replaced by the early-return
          `<MobileDataTable>` at the top of the function so the
          desktop tree below is the *only* thing rendered on a
          desktop viewport. */}

      {/* ───────────────────────── Footer ───────────────────────── */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-xs text-muted-foreground">
          {enableRowSelection && selectedCount > 0
            ? `${selectedCount} of ${table.getFilteredRowModel().rows.length} row(s) selected`
            : `${table.getFilteredRowModel().rows.length} row(s)`}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>Rows per page</span>
            <Select
              value={String(table.getState().pagination.pageSize)}
              onValueChange={(v) => table.setPageSize(Number(v))}
            >
              <SelectTrigger className="h-8 w-20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizes.map((s) => (
                  <SelectItem key={s} value={String(s)}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="text-xs text-muted-foreground">
            Page {table.getState().pagination.pageIndex + 1} of{' '}
            {Math.max(1, table.getPageCount())}
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
              aria-label="First page"
              className="h-8 w-8"
            >
              <ChevronsLeft className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              aria-label="Previous page"
              className="h-8 w-8"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              aria-label="Next page"
              className="h-8 w-8"
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
              aria-label="Last page"
              className="h-8 w-8"
            >
              <ChevronsRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SortIcon({ dir }: { dir: false | 'asc' | 'desc' }) {
  if (dir === 'asc') return <ArrowUp className="h-3 w-3" />;
  if (dir === 'desc') return <ArrowDown className="h-3 w-3" />;
  return <ArrowUpDown className="h-3 w-3 text-muted-foreground/50" />;
}

// ────────────────────────────────────────────────────────────────────
// Mobile sub-view — Asana / Linear / Notion style card list with a
// toolbar (search + sort + filter), bottom-sheet filter
// picker, "Load more" pagination, and a floating bulk-action bar
// when selection is active.
//
// Reads from the same `useReactTable` instance the desktop tree uses
// so a portrait→landscape rotation keeps every filter, sort, and
// selected row in sync. `getSortedRowModel()` is used instead of
// `getRowModel()` because the latter has the desktop's page-of-25
// truncation applied — on mobile we want the full filtered set so
// the load-more pager can step through it in its own slices.
// ────────────────────────────────────────────────────────────────────
interface MobileDataTableProps<TData> {
  table: TanstackTable<TData>;
  isLoading?: boolean;
  searchPlaceholder?: string | null;
  facetedFilters: FacetedFilter[];
  enableRowSelection: boolean;
  enableTextWrapToggle: boolean;
  textWrap: boolean;
  setTextWrap: React.Dispatch<React.SetStateAction<boolean>>;
  onRowClick?: (row: TData) => void;
  rowClassName?: (row: TData) => string | undefined;
  emptyState?: React.ReactNode;
  mobileCard?: MobileCardConfig<TData>;
  mobileRenderer?: (row: TData) => React.ReactNode;
  mobileInitialCount: number;
  mobileLoadMoreStep: number;
  bulkActions?: (table: TanstackTable<TData>) => React.ReactNode;
  pageActions?: React.ReactNode;
  toolbarSlot?: React.ReactNode;
  onReset: () => void;
}

function MobileDataTable<TData>({
  table,
  isLoading,
  searchPlaceholder,
  facetedFilters,
  enableRowSelection,
  enableTextWrapToggle,
  textWrap,
  setTextWrap,
  onRowClick,
  rowClassName,
  emptyState,
  mobileCard,
  mobileRenderer,
  mobileInitialCount,
  mobileLoadMoreStep,
  bulkActions,
  pageActions,
  toolbarSlot,
  onReset,
}: MobileDataTableProps<TData>) {
  // Track how many rows the operator has revealed via "Load more".
  // Resets to the initial count whenever the filter / sort changes
  // so a fresh filter doesn't display 500 rows from the previous
  // facet.
  const [shownCount, setShownCount] = useState(mobileInitialCount);
  const globalFilter = table.getState().globalFilter as string | undefined;
  const sorting = table.getState().sorting;
  const columnFilters = table.getState().columnFilters;

  // Serialize sort + column filters so the effect deps are stable
  // primitives rather than fresh array references — keeps the hook
  // from firing on every render and satisfies the linter at the
  // same time.
  const sortingKey = JSON.stringify(sorting);
  const columnFiltersKey = JSON.stringify(columnFilters);
  useEffect(() => {
    setShownCount(mobileInitialCount);
  }, [mobileInitialCount, globalFilter, sortingKey, columnFiltersKey]);

  const allFilteredRows = table.getSortedRowModel().rows;
  const visibleRows = allFilteredRows.slice(0, shownCount);
  const totalFiltered = allFilteredRows.length;

  // ── Selection mode ───────────────────────────────────────────
  // We toggle "selection mode" implicitly: once any row is
  // selected, every card shows the visual checkbox in its leading
  // slot. Long-press on any card from a fresh page promotes the
  // single tapped row into the selection set (Asana's entry
  // gesture). The desktop checkbox column is hidden on mobile
  // because its hit target is too small.
  const selection = table.getState().rowSelection;
  const selectionCount = Object.keys(selection).length;
  const selectionMode = enableRowSelection && selectionCount > 0;

  // ── Sort options for the bottom sheet ────────────────────────
  // Every sortable column shows up as a tap target. We rank them
  // by visible order so the operator sees them in the same
  // arrangement they'd find in the desktop header.
  const columnVisibilityState = table.getState().columnVisibility;
  const sortOptions: MobileSortOption[] = useMemo(() => {
    return table
      .getAllLeafColumns()
      .filter(
        (c) =>
          c.getCanSort() &&
          !c.id.startsWith(SYSTEM_COLUMN_PREFIX) &&
          c.getIsVisible(),
      )
      .map((c) => ({
        id: c.id,
        label:
          typeof c.columnDef.header === 'string'
            ? c.columnDef.header
            : c.id,
      }));
    // Recompute when visibility flips so a hidden column drops
    // out of the picker.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, columnVisibilityState]);

  const firstSort = sorting[0];
  const activeSortId = firstSort?.id ?? null;
  const activeSortDir: 'asc' | 'desc' | null = firstSort
    ? firstSort.desc
      ? 'desc'
      : 'asc'
    : null;

  // ── Filter groups (faceted) — re-shaped for MobileTableShell ─
  const filterGroups: MobileFilterGroup[] = useMemo(() => {
    const groups: MobileFilterGroup[] = [];
    for (const f of facetedFilters) {
      const col = table.getColumn(f.columnId);
      if (!col) continue;
      const facets = col.getFacetedUniqueValues();
      const explicit = f.options;
      const opts =
        explicit ??
        Array.from(facets.keys())
          .filter((v): v is string => typeof v === 'string' && v.length > 0)
          .sort()
          .map((v) => {
            const formatted = f.formatOption?.(v);
            return {
              value: v,
              label: formatted?.label ?? v,
              icon: formatted?.icon,
            };
          });

      const selected = (col.getFilterValue() as string[] | undefined) ?? [];
      const options = opts.map((o) => {
        const count = facets.get(o.value);
        return count !== undefined
          ? { ...o, count }
          : { ...o };
      });
      groups.push({
        id: f.columnId,
        title: f.title,
        options,
        selected,
        onChange: (next: string[]) =>
          col.setFilterValue(next.length > 0 ? next : undefined),
      });
    }
    return groups;
    // We deliberately depend on `columnFiltersKey` (a JSON-serialised
    // snapshot) instead of `columnFilters` so a fresh array identity
    // from TanStack doesn't keep recomputing this. `allFilteredRows`
    // affects facet counts so we include its length.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facetedFilters, table, columnFiltersKey, allFilteredRows.length]);

  // ── Column visibility — re-shaped for the same bottom sheet ──
  const columnVisibility = useMemo(
    () =>
      table
        .getAllLeafColumns()
        .filter(
          (c) =>
            c.getCanHide() && !c.id.startsWith(SYSTEM_COLUMN_PREFIX),
        )
        .map((c) => ({
          id: c.id,
          label:
            typeof c.columnDef.header === 'string'
              ? c.columnDef.header
              : c.id,
          visible: c.getIsVisible(),
          onToggle: (v: boolean) => c.toggleVisibility(v),
        })),
    // Pull state.columnVisibility so the bottom-sheet chips
    // re-render with the new selection without a full table tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [table, columnVisibilityState],
  );

  // ── Per-row card mapper ──────────────────────────────────────
  // 1. If the caller supplied a freeform `mobileRenderer`, honour
  //    it (back-compat). Wrap in MobileListCard chrome so
  //    selection + long-press still work.
  // 2. Else if `mobileCard` config exists, drive every slot from it.
  // 3. Else fall back to auto-deriving title / subtitle from the
  //    first visible non-system columns so an un-configured page
  //    still produces a usable mobile experience.
  const autoTitleColumn = useMemo(() => {
    return table
      .getAllLeafColumns()
      .find(
        (c) =>
          c.getIsVisible() && !c.id.startsWith(SYSTEM_COLUMN_PREFIX),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, columnVisibilityState]);

  return (
    <MobileTableShell
      searchPlaceholder={searchPlaceholder}
      searchValue={globalFilter ?? ''}
      onSearchChange={(v) => table.setGlobalFilter(v)}
      sortOptions={sortOptions}
      activeSortId={activeSortId}
      activeSortDir={activeSortDir}
      onSortChange={(id, dir) => {
        if (!id || !dir) {
          table.setSorting([]);
          return;
        }
        table.setSorting([{ id, desc: dir === 'desc' }]);
      }}
      filterGroups={filterGroups}
      columnVisibility={columnVisibility}
      textWrap={
        enableTextWrapToggle
          ? { value: textWrap, onChange: (v) => setTextWrap(v) }
          : undefined
      }
      onReset={onReset}
      selectionCount={selectionCount}
      onClearSelection={() => table.resetRowSelection()}
      bulkActions={bulkActions?.(table)}
      totalCount={totalFiltered}
      visibleCount={Math.min(shownCount, totalFiltered)}
      onLoadMore={() => setShownCount((c) => c + mobileLoadMoreStep)}
      hasMore={shownCount < totalFiltered}
      pageActions={
        // Hide the page-actions slot when selection is active so
        // the floating bulk-action bar is the unambiguous primary
        // surface. Toolbar-slot still shows because that's where
        // page-level toggles like "Read-only banner" filter chips
        // live.
        selectionMode ? toolbarSlot : (
          <>
            {pageActions}
            {toolbarSlot}
          </>
        )
      }
    >
      {isLoading ? (
        <div className="py-12 text-center text-sm text-muted-foreground">
          Loading…
        </div>
      ) : visibleRows.length === 0 ? (
        <div className="py-12 text-center">
          {emptyState ?? (
            <span className="text-sm text-muted-foreground">No results.</span>
          )}
        </div>
      ) : (
        <MobileListCardStack>
          {visibleRows.map((row) => {
            const original = row.original;
            // Custom render takes precedence (back-compat).
            if (mobileRenderer) {
              return (
                <MobileListCard
                  key={row.id}
                  title={
                    <div className="font-normal">
                      {mobileRenderer(original)}
                    </div>
                  }
                  onTap={onRowClick ? () => onRowClick(original) : undefined}
                  onLongPress={
                    enableRowSelection
                      ? () => row.toggleSelected(true)
                      : undefined
                  }
                  selected={row.getIsSelected()}
                  onToggleSelect={
                    enableRowSelection
                      ? () => row.toggleSelected(!row.getIsSelected())
                      : undefined
                  }
                  selectionMode={selectionMode}
                  dimmed={
                    rowClassName?.(original)?.includes('bg-bright-moss-dark')
                  }
                  className={rowClassName?.(original)}
                />
              );
            }

            // Structured config path.
            const cfg = mobileCard;
            // Fallback title: pull the first visible non-system
            // column's value off this row. We use the row's own
            // `getValue(colId)` because `Column.getValue` is not a
            // thing in TanStack v8 — values are addressed via the
            // row, not the column definition.
            const fallbackTitle = autoTitleColumn
              ? (row.getValue(autoTitleColumn.id) as React.ReactNode) ?? '—'
              : '—';
            const title = cfg?.title?.(original) ?? fallbackTitle;
            const subtitle = cfg?.subtitle?.(original);
            const badges = cfg?.badges?.(original);
            const meta = cfg?.meta?.(original);
            const amount = cfg?.amount?.(original) ?? undefined;
            const leading = cfg?.leading?.(original);
            const trailing = cfg?.trailing?.(original);
            const dimmed =
              cfg?.dimmed?.(original) ??
              rowClassName?.(original)?.includes('bg-bright-moss-dark');

            // Callers commonly compose the badge array as
            // `[<Badge/>, row.category && <Badge/>]`, which slots a
            // falsy value into the array when the condition is
            // false. Strip those out so the chip row doesn't render
            // a stray `null` and `<span className="contents">` with
            // nothing inside it.
            const renderableBadges = badges?.filter(Boolean) ?? [];

            return (
              <MobileListCard
                key={row.id}
                title={title}
                subtitle={subtitle}
                badges={
                  renderableBadges.length > 0 ? (
                    <>
                      {renderableBadges.map((b, i) => (
                        <span key={i} className="contents">
                          {b}
                        </span>
                      ))}
                    </>
                  ) : undefined
                }
                meta={meta}
                amount={amount ?? undefined}
                leading={leading}
                trailing={trailing}
                onTap={onRowClick ? () => onRowClick(original) : undefined}
                onLongPress={
                  enableRowSelection
                    ? () => row.toggleSelected(true)
                    : undefined
                }
                selected={row.getIsSelected()}
                onToggleSelect={
                  enableRowSelection
                    ? () => row.toggleSelected(!row.getIsSelected())
                    : undefined
                }
                selectionMode={selectionMode}
                dimmed={dimmed}
                className={rowClassName?.(original)}
              />
            );
          })}
        </MobileListCardStack>
      )}
    </MobileTableShell>
  );
}

/**
 * Faceted multi-select filter dropdown. Pulls unique values from the
 * column's facet model, lets the operator tick the ones they want, and
 * writes the result back as a column filter that pairs with the
 * `arrIncludesSome` filter fn declared on the column definition.
 */
function FacetedFilterDropdown<TData>({
  table,
  filter,
}: {
  table: TanstackTable<TData>;
  filter: FacetedFilter;
}) {
  const column = table.getColumn(filter.columnId);

  // Hooks must run unconditionally — derive `facets` defensively so the
  // useMemo below can stay above the null-guard.
  const facets = column?.getFacetedUniqueValues() ?? new Map<unknown, number>();
  const selected = new Set(
    (column?.getFilterValue() as string[] | undefined) ?? [],
  );

  // `facets` is a fresh Map every render but its content only changes
  // when the underlying data does; treating it as a structural dep here
  // is intentional — the dropdown is cheap to recompute.
  const options = useMemo(() => {
    if (filter.options) return filter.options;
    return Array.from(facets.keys())
      .filter((v): v is string => typeof v === 'string' && v.length > 0)
      .sort()
      .map((v) => {
        const formatted = filter.formatOption?.(v);
        return { value: v, label: formatted?.label ?? v, icon: formatted?.icon };
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facets.size, filter]);

  if (!column) return null;

  const toggle = (value: string) => {
    const next = new Set(selected);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    column.setFilterValue(next.size > 0 ? Array.from(next) : undefined);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="border-dashed">
          <ListFilter className="h-3.5 w-3.5" />
          {filter.title}
          {selected.size > 0 && (
            <>
              <span className="mx-1 h-3.5 w-px bg-border" />
              <Badge variant="default" className="rounded px-1.5 py-0">
                {selected.size}
              </Badge>
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel>{filter.title}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {options.length === 0 ? (
          <div className="px-2 py-1.5 text-xs text-muted-foreground">No values.</div>
        ) : (
          options.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => toggle(o.value)}
              className="flex w-full items-center gap-2 px-2 py-1.5 text-sm hover:bg-secondary/60"
            >
              <span
                className={cn(
                  'flex h-4 w-4 items-center justify-center rounded border border-border',
                  selected.has(o.value)
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'opacity-60',
                )}
              >
                {selected.has(o.value) && <Check className="h-3 w-3" />}
              </span>
              {o.icon}
              <span className="flex-1 truncate">{o.label}</span>
              <span className="text-xs text-muted-foreground">
                {facets.get(o.value) ?? 0}
              </span>
            </button>
          ))
        )}
        {selected.size > 0 && (
          <Fragment>
            <DropdownMenuSeparator />
            <button
              type="button"
              onClick={() => column.setFilterValue(undefined)}
              className="flex w-full items-center gap-2 px-2 py-1.5 text-xs text-muted-foreground hover:bg-secondary/60"
            >
              <EyeOff className="h-3 w-3" />
              Clear filter
            </button>
          </Fragment>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Horizontal scrollbar mirrored above a scroll container, so wide tables
 * can be scrolled sideways without first scrolling to the bottom of the
 * page. Hidden when the target doesn't overflow horizontally.
 */
function TopScrollbar({ targetRef }: { targetRef: RefObject<HTMLDivElement | null> }) {
  const barRef = useRef<HTMLDivElement>(null);
  const [scrollWidth, setScrollWidth] = useState(0);
  const [overflowing, setOverflowing] = useState(false);

  useEffect(() => {
    const target = targetRef.current;
    const bar = barRef.current;
    if (!target || !bar) return;

    const measure = () => {
      setScrollWidth(target.scrollWidth);
      setOverflowing(target.scrollWidth > target.clientWidth + 1);
    };
    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(target);
    if (target.firstElementChild) ro.observe(target.firstElementChild);

    // Only write when the values differ so the echoed scroll event from
    // the other element is a no-op instead of a feedback loop.
    const onBarScroll = () => {
      if (target.scrollLeft !== bar.scrollLeft) target.scrollLeft = bar.scrollLeft;
    };
    const onTargetScroll = () => {
      if (bar.scrollLeft !== target.scrollLeft) bar.scrollLeft = target.scrollLeft;
    };
    bar.addEventListener('scroll', onBarScroll, { passive: true });
    target.addEventListener('scroll', onTargetScroll, { passive: true });

    return () => {
      ro.disconnect();
      bar.removeEventListener('scroll', onBarScroll);
      target.removeEventListener('scroll', onTargetScroll);
    };
  }, [targetRef]);

  useEffect(() => {
    if (barRef.current && targetRef.current) {
      barRef.current.scrollLeft = targetRef.current.scrollLeft;
    }
  }, [targetRef, overflowing, scrollWidth]);

  return (
    <div
      ref={barRef}
      aria-hidden="true"
      className={cn('overflow-x-auto overflow-y-hidden border-b border-border', !overflowing && 'hidden')}
    >
      <div style={{ width: scrollWidth, height: 1 }} />
    </div>
  );
}

/** Helper for column defs: `filterFn: arrIncludesSome`. Use with faceted filters. */
export function arrIncludesSome(
  row: { getValue: (id: string) => unknown },
  id: string,
  filterValue: string[],
): boolean {
  if (!filterValue?.length) return true;
  const value = row.getValue(id);
  if (Array.isArray(value)) return value.some((v) => filterValue.includes(String(v)));
  return filterValue.includes(String(value ?? ''));
}
